import React, { useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Modal, Button, Toggle } from '../../components/common';
import { galleryService } from '../../services/galleryService';

const EMPTY_DRAFT = { name: '', sortOrder: '' };

/**
 * Add, rename, reorder, hide and delete gallery categories.
 * Edits and delete confirmations happen inline: a ConfirmDialog stacked on
 * this modal would close both on Escape, since each Modal listens globally.
 *
 * @param categories  normalized categories, already sorted by the API
 * @param onChanged   refetch after any change (photo counts and filters depend on it)
 */
const GalleryCategoriesModal = ({ isOpen, onClose, categories, onChanged }) => {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, sortOrder }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const handleClose = () => {
    setDraft(EMPTY_DRAFT);
    setEditing(null);
    setConfirmDeleteId(null);
    onClose();
  };

  const reportError = (error, fallback) => {
    const { status, data } = error.response ?? {};
    if (status === 422 && data?.errors) {
      toast.error(Object.values(data.errors).flat()[0] || fallback);
    } else if (status === 404 || status === 409) {
      toast.error(data?.message || fallback);
      onChanged();
    } else if (status !== 500) {
      // 500s are already toasted by the api interceptor.
      toast.error(fallback);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    setAdding(true);
    try {
      await galleryService.createCategory({ name: draft.name.trim(), sortOrder: draft.sortOrder });
      toast.success('Category added');
      setDraft(EMPTY_DRAFT);
      onChanged();
    } catch (error) {
      reportError(error, 'Failed to add category');
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async (category) => {
    if (!editing.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    setBusyId(category.id);
    try {
      await galleryService.updateCategory(category.id, {
        name: editing.name.trim(),
        sortOrder: editing.sortOrder,
        isActive: category.isActive,
      });
      toast.success('Category updated');
      setEditing(null);
      onChanged();
    } catch (error) {
      reportError(error, 'Failed to update category');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = async (category) => {
    setBusyId(category.id);
    try {
      await galleryService.toggleCategoryStatus(category.id);
      toast.success(category.isActive ? 'Category hidden from the website' : 'Category shown on the website');
      onChanged();
    } catch (error) {
      reportError(error, 'Failed to update category');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (category) => {
    setBusyId(category.id);
    try {
      await galleryService.deleteCategory(category.id);
      toast.success('Category deleted');
      setConfirmDeleteId(null);
      onChanged();
    } catch (error) {
      reportError(error, 'Failed to delete category');
    } finally {
      setBusyId(null);
    }
  };

  const renderRow = (category) => {
    const isEditing = editing?.id === category.id;
    const isConfirming = confirmDeleteId === category.id;
    const busy = busyId === category.id;

    return (
      <li key={category.id} className={`gcat-row ${category.isActive ? '' : 'is-inactive'}`}>
        {isEditing ? (
          <div className="gcat-edit">
            <input
              type="text"
              className="gcat-input"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(category)}
              aria-label="Category name"
              maxLength={100}
              autoFocus
            />
            <input
              type="number"
              className="gcat-input gcat-order"
              value={editing.sortOrder}
              onChange={(e) => setEditing({ ...editing, sortOrder: e.target.value })}
              aria-label="Sort order"
              min="0"
            />
          </div>
        ) : (
          <div className="gcat-identity">
            <span className="gcat-name">{category.name}</span>
            <span className="gcat-meta">
              {category.imagesCount} {category.imagesCount === 1 ? 'photo' : 'photos'} · order {category.sortOrder}
              {!category.isActive && ' · hidden'}
            </span>
          </div>
        )}

        <div className="gcat-actions">
          {isEditing ? (
            <>
              <button type="button" className="action-btn edit" title="Save" onClick={() => handleSaveEdit(category)} disabled={busy}>
                <FiCheck />
              </button>
              <button type="button" className="action-btn" title="Cancel" onClick={() => setEditing(null)}>
                <FiX />
              </button>
            </>
          ) : isConfirming ? (
            <>
              <span className="gcat-confirm-text">Delete?</span>
              <button type="button" className="action-btn delete" title="Confirm delete" onClick={() => handleDelete(category)} disabled={busy}>
                <FiCheck />
              </button>
              <button type="button" className="action-btn" title="Cancel" onClick={() => setConfirmDeleteId(null)}>
                <FiX />
              </button>
            </>
          ) : (
            <>
              <Toggle
                name={`gcat-status-${category.id}`}
                checked={category.isActive}
                onChange={() => handleToggle(category)}
                disabled={busy}
                size="small"
              />
              <button
                type="button"
                className="action-btn edit"
                title="Edit"
                onClick={() => {
                  setConfirmDeleteId(null);
                  setEditing({ id: category.id, name: category.name, sortOrder: String(category.sortOrder) });
                }}
              >
                <FiEdit2 />
              </button>
              <button
                type="button"
                className="action-btn delete"
                title={category.imagesCount > 0 ? 'Move or delete its photos first' : 'Delete'}
                onClick={() => {
                  setEditing(null);
                  setConfirmDeleteId(category.id);
                }}
                disabled={category.imagesCount > 0}
              >
                <FiTrash2 />
              </button>
            </>
          )}
        </div>
      </li>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Gallery Categories"
      size="medium"
      footer={<Button variant="ghost" onClick={handleClose}>Done</Button>}
    >
      <form className="gcat-add" onSubmit={handleAdd}>
        <input
          type="text"
          className="gcat-input"
          placeholder="New category, e.g. Rooftop solar"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          aria-label="New category name"
          maxLength={100}
        />
        <input
          type="number"
          className="gcat-input gcat-order"
          placeholder="Order"
          value={draft.sortOrder}
          onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })}
          aria-label="Sort order"
          min="0"
        />
        <Button type="submit" icon={<FiPlus />} loading={adding}>
          Add
        </Button>
      </form>

      {categories.length === 0 ? (
        <p className="gcat-empty">No categories yet. Add one above, then upload photos into it.</p>
      ) : (
        <ul className="gcat-list">{categories.map(renderRow)}</ul>
      )}

      <p className="gallery-hint gcat-hint">
        Categories become the filter buttons on the website gallery, lowest order first.
        Hiding a category also hides its photos. A category can only be deleted once it is empty.
      </p>
    </Modal>
  );
};

export default GalleryCategoriesModal;
